import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { CircularProgress } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { GridRenderEditCellParams, GridRowModel } from "@mui/x-data-grid";
import isEmpty from "lodash/isEmpty";
import React, { ReactElement } from "react";

import { M_DASH_UNICODE } from "../../../../../utils/constants/constants";
import { FolderContentRequestPayload } from "../../../../../utils/types/documents.type";
import { Option } from "../../../../../utils/types/filter.type";
import useDocumentsContext from "../../../hooks/useDocumentsContext.hooks";
import EditSelection from "../../editSelection/EditSelection";
import { SelectBox } from "./DocumentsGridCells.styles";

interface SelectionBoxProps {
  anchorEl: HTMLDivElement | null;
  data: GridRowModel;
  handleLoading: (status: boolean) => void;
  handleClose: () => void;
  options: Array<any>;
  update: Function;
  selected: Array<string>;
  updatedValues: Function;
  field: string;
  payload: FolderContentRequestPayload;
}

type Investor = {
  id: string;
  name: string;
};

const isMulti = true;

export const SelectionBox = ({
  anchorEl,
  data,
  handleLoading,
  handleClose,
  options,
  update,
  selected,
  updatedValues,
  payload,
}: SelectionBoxProps) => {

  const handleUpdate = (selected: string[] | string | Option, selectedOptions?: Option[]) => {
    
    const params = {
      ...data,
      // @ts-ignore:next-line
      investorIds: Array.isArray(selected) ? selected : [selected?.id],
    };

    const requiredParams = [
      "index",
      "folderId",
      "clientId",
      "fundIds",
      "investorIds",
      "quarter",
      "published",
      "sizeInBytes",
      "fileType",
      "name",
      "id",
    ];

    const requestBody: any = {};

    for(const index of requiredParams) {
      requestBody[index] = index === "fundIds" ? params["funds"].map(({ id }: any) => id): params[index];
    }

    handleLoading(true);

    if(requestBody.clientId && requestBody.folderId && requestBody.id) {
      update(data.id, requestBody, payload)
        .then(() => {
          handleLoading(false);
          handleClose();
          selectedOptions && updatedValues(selectedOptions);
        })
        .catch(() => {
          handleLoading(false);
          handleClose();
        });
    }
  };

  return (
    anchorEl ? <EditSelection
      options={options}
      handleUpdate={handleUpdate}
      handleOnClose={handleClose}
      anchorEl={anchorEl}
      selected={selected}
      selectAllText="Select All Investors"
      showFilterAll={true}
      isMulti={false}
      isAllSelected={true}
      applyOnSelect={true}
    />: <></>
  );
};

interface InvestorCellProps {
  data: GridRowModel;
  items: Investor[];
}

function InvestorCell({ 
  data,
  items
}: InvestorCellProps): React.ReactElement {

  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selection, setSelection] = React.useState<Option[]>([]);

  const { state, updateFiles } = useDocumentsContext();

  const handleOnClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ): void => {
    event?.preventDefault();
    event?.stopPropagation();
    if(state?.isPageLocked) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleLoading = (status: boolean) => setLoading(status);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdatedSelection = (selectedOptions: Option[]) => {
    setSelection(selectedOptions);
  };

  const options = state?.filtersData.investors.filter(investor => investor.id !== "(none)") || [];

  React.useEffect(() => {
    let selectedOptions = [];

    if(isMulti && data.investors) {
      if(Array.isArray(data.investors)) {
        selectedOptions = data.investors;
      } else {
        selectedOptions = [data.investors];
      }
    }
    setSelection(selectedOptions);
  }, [data.investors]);

  const selectionIDs = selection.map((item: any) => item.id);

  const renderComponent = React.useMemo(() => {
    if((data.type === "folder") || isEmpty(selection)) {
      return <Typography variant="cells">{M_DASH_UNICODE}</Typography>;
    }
    else if(selection.length > 0) {
      return (selection.length === 1) ? 
        <Typography variant="cells">{selection[0].name}</Typography>:
        <Typography variant="cells">Multiple Selected</Typography>;
    }
    return <Typography variant="cells">{M_DASH_UNICODE }</Typography>;
  }, [selection]);

  const filters: any = { ...(state?.selectedFilters || {}) };

  if(state?.selectedFilters["funds"].length === state?.filtersData["funds"].length) {
    filters.funds = [];
  }
  if(state?.selectedFilters["investors"].length === state?.filtersData["investors"].length) {
    filters.investors = [];
  }
  if(state?.selectedFilters["quarters"].length === state?.filtersData["quarters"].length) {
    filters.quarters = [];
  }

  filters.searchText = state?.selectedFilters.searchText || "";

  const payload = {
    page: state?.pagination.page || 1,
    pageSize: state?.pagination.pageSize || 100,
    ...filters,
    sort: state?.sort || [],
  };

    return (
      data.isSelected ? (
        !loading ? <>
          <SelectBox
            onClick={handleOnClick}
          >
            <Box>{renderComponent}</Box>
            <span>
              <ArrowDropDown />
            </span>
          </SelectBox>
            {data.type === "file" && <SelectionBox
              anchorEl={anchorEl}
              handleLoading={handleLoading}
              update={updateFiles}
              handleClose={handleClose}
              data={data}
              options={options}
              selected={selectionIDs}
              updatedValues={handleUpdatedSelection}
              field="investors"
              payload={payload}
            />}
          </>: <CircularProgress />): <Box>
        {renderComponent}
      </Box>
    );
}

const getValue = (fieldName: string, rowValues: GridRowModel) => {
  const childProperty: string[] = fieldName.split(".");

  if (!rowValues[childProperty[0]]) return;

  switch (childProperty.length) {
    case 2:
      return rowValues[childProperty[0]][childProperty[1]];
    case 3:
      return rowValues[childProperty[0]][childProperty[1]][childProperty[2]];
    default:
      return rowValues[fieldName];
  }
};

function InvestorCellRenderer({ row }: GridRenderEditCellParams): ReactElement {
  const value: Investor[] = getValue("investors", row);

  const renderInvestor = React.useMemo(() => <InvestorCell
    data={row}
    items={value}
  />, [value, row.isSelected, row.quarter, row.funds, row.published]);

  return renderInvestor;
}

export default InvestorCellRenderer;
